import React, { lazy } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import PortalProtectedRoute from '../portal/components/PortalProtectedRoute';
import RequireFeature from '../components/RequireFeature';

// Portal layouts (eager — needed immediately on portal mount)
import TenantLayout from '../portal/tenant/TenantLayout';
import OwnerLayout from '../portal/owner/OwnerLayout';
import VendorLayout from '../portal/vendor/VendorLayout';

// Portal login pages (eager — shown before auth)
import TenantLogin from '../portal/tenant/TenantLogin';
import OwnerLogin from '../portal/owner/OwnerLogin';
import SetupPassword from '../portal/SetupPassword';
import VendorSetupPassword from '../portal/vendor/VendorSetupPassword';

// ── Tenant pages (lazy) ────────────────────────────────────────────────────
const TenantDashboard = lazy(() => import('../portal/tenant/TenantDashboard'));
const TenantPayments = lazy(() => import('../portal/tenant/TenantPayments'));
const TenantDocuments = lazy(() => import('../portal/tenant/TenantDocuments'));
const TenantMaintenance = lazy(() => import('../portal/tenant/TenantMaintenance'));
const TenantAnnouncements = lazy(() => import('../portal/tenant/TenantAnnouncements'));
const TenantLease = lazy(() => import('../portal/tenant/TenantLease'));
const TenantServices = lazy(() => import('../portal/tenant/TenantServices'));
const TenantProfile = lazy(() => import('../portal/tenant/TenantProfile'));
const TenantInspections = lazy(() => import('../portal/tenant/TenantInspections'));
const TenantMessages = lazy(() => import('../portal/tenant/TenantMessages'));
const TenantViolations = lazy(() => import('../portal/tenant/TenantViolations'));
const TenantUtilities = lazy(() => import('../portal/tenant/TenantUtilities'));
const TenantAmenityBooking = lazy(() => import('../portal/tenant/TenantAmenityBooking'));
const TenantLeaseHistory = lazy(() => import('../portal/tenant/TenantLeaseHistory'));
const TenantRenewals = lazy(() => import('../portal/tenant/TenantRenewals'));
const TenantSurvey = lazy(() => import('../portal/tenant/TenantSurvey'));

// ── Owner pages (lazy) ─────────────────────────────────────────────────────
const OwnerDashboard = lazy(() => import('../portal/owner/OwnerDashboard'));
const OwnerProperties = lazy(() => import('../portal/owner/OwnerProperties'));
const OwnerContracts = lazy(() => import('../portal/owner/OwnerContracts'));
const OwnerContractSign = lazy(() => import('../portal/owner/OwnerContractSign'));
const OwnerContractDetails = lazy(() => import('../portal/owner/OwnerContractDetails'));
const OwnerInstallments = lazy(() => import('../portal/owner/OwnerInstallments'));
const OwnerFinancialsPortal = lazy(() => import('../portal/owner/OwnerFinancials'));
const OwnerMaintenance = lazy(() => import('../portal/owner/OwnerMaintenance'));
const OwnerServices = lazy(() => import('../portal/owner/OwnerServices'));
const OwnerProfile = lazy(() => import('../portal/owner/OwnerProfile'));
const OwnerMessages = lazy(() => import('../portal/owner/OwnerMessages'));
const OwnerAnnouncements = lazy(() => import('../portal/owner/OwnerAnnouncements'));
const OwnerLedgerPortal = lazy(() => import('../portal/owner/OwnerLedger'));
const OwnerTaxDocuments = lazy(() => import('../portal/owner/OwnerTaxDocuments'));
const OwnerPropertyReport = lazy(() => import('../portal/owner/OwnerPropertyReport'));
const OwnerInvestmentDashboard = lazy(() => import('../portal/owner/OwnerInvestmentDashboard'));

// ── Vendor pages (lazy) ────────────────────────────────────────────────────
const VendorDashboard = lazy(() => import('../portal/vendor/VendorDashboard'));
const VendorWorkOrders = lazy(() => import('../portal/vendor/VendorWorkOrders'));
const VendorProfile = lazy(() => import('../portal/vendor/VendorProfile'));
const VendorW9Form = lazy(() => import('../portal/vendor/VendorW9Form'));


export default function PortalRoutes() {
  return (
    <Routes>
      <Route path="setup-password" element={<SetupPassword />} />
      <Route path="tenant/login" element={<TenantLogin />} />
      <Route path="owner/login" element={<OwnerLogin />} />
      <Route path="vendor/setup-password" element={<VendorSetupPassword />} />

      {/* Vendor Portal */}
      <Route path="vendor/*" element={
        <PortalProtectedRoute requiredRole="vendor">
          <VendorLayout />
        </PortalProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<VendorDashboard />} />
        <Route path="work-orders" element={<VendorWorkOrders />} />
        <Route path="profile" element={<VendorProfile />} />
        <Route path="w9" element={<VendorW9Form />} />
      </Route>

      {/* Tenant Portal */}
      <Route path="tenant/*" element={
        <PortalProtectedRoute requiredRole="tenant">
          <RequireFeature feature="portals">
            <TenantLayout />
          </RequireFeature>
        </PortalProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<TenantDashboard />} />
        <Route path="payments" element={<TenantPayments />} />
        <Route path="maintenance" element={<TenantMaintenance />} />
        <Route path="documents" element={<TenantDocuments />} />
        <Route path="announcements" element={<TenantAnnouncements />} />
        <Route path="lease" element={<TenantLease />} />
        <Route path="services" element={<TenantServices />} />
        <Route path="profile" element={<TenantProfile />} />
        <Route path="inspections" element={<TenantInspections />} />
        <Route path="messages" element={<TenantMessages />} />
        <Route path="violations" element={<TenantViolations />} />
        <Route path="utilities" element={<TenantUtilities />} />
        <Route path="amenities" element={<TenantAmenityBooking />} />
        <Route path="leases" element={<TenantLeaseHistory />} />
        <Route path="renewals" element={<TenantRenewals />} />
        <Route path="survey" element={<TenantSurvey />} />
      </Route>

      {/* Owner Portal */}
      <Route path="owner/*" element={
        <PortalProtectedRoute requiredRole="owner">
          <RequireFeature feature="portals">
            <OwnerLayout />
          </RequireFeature>
        </PortalProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<OwnerDashboard />} />
        <Route path="properties" element={<OwnerProperties />} />
        <Route path="contracts" element={<OwnerContracts />} />
        <Route path="contracts/:contractId/details" element={<OwnerContractDetails />} />
        <Route path="contracts/:contractId/sign" element={<OwnerContractSign />} />
        <Route path="installments" element={<OwnerInstallments />} />
        <Route path="financials" element={<OwnerFinancialsPortal />} />
        <Route path="maintenance" element={<OwnerMaintenance />} />
        <Route path="services" element={<OwnerServices />} />
        <Route path="profile" element={<OwnerProfile />} />
        <Route path="messages" element={<OwnerMessages />} />
        <Route path="announcements" element={<OwnerAnnouncements />} />
        <Route path="investment" element={<OwnerInvestmentDashboard />} />
        <Route path="ledger" element={<OwnerLedgerPortal />} />
        <Route path="tax-documents" element={<OwnerTaxDocuments />} />
        <Route path="property-report" element={<OwnerPropertyReport />} />
      </Route>
    </Routes>
  );
}
